import React, { useState } from "react";
import AuthenticationPageLayout from "./AuthenticationPageLayout";
import { BaseButton, Card } from "@velocity-ehs/component-library";
import { Input } from "../../sharedComponents/inputs/Input";
import { useForm } from "react-hook-form";
import {
  passwordRule,
  validatePassword,
} from "../../supportFunctions/ValidationHelpers";
import classes from "./CreatePassword.module.scss";
import { useSnackbarActionContext } from "../../../context/SnackbarContext";
import { SnackbarType } from "../../sharedComponents/Snackbar";
import { createFormData, AUTH_API, getLoginUrl } from "./HelperFunctions";
import { useTranslation } from "react-i18next";

type FormFields = {
  password: string;
  reenter: string;
};

const CreatePassword: React.FC = () => {
  const [successfulUpdate, setSuccessfulUpdate] = useState(false);
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();

  const queryString = new URLSearchParams(window.location.search);
  const urlParams = {
    userId: queryString.get("userId"),
    token: queryString.get("token"),
    domain: queryString.get("domain"),
    username: queryString.get("username"),
    code: queryString.get("code"),
  };

  const {
    handleSubmit,
    control,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    mode: "onBlur",
    defaultValues: {
      password: "",
      reenter: "",
    },
  });

  const onSubmit = async (values: FormFields) => {
    const endpoint = urlParams.code ? "resetWithCode" : "resetWithToken";
    const formData = createFormData({
      ...urlParams,
      newPassword: values.password,
    });
    try {
      await AUTH_API.post(`/resetpassword/${endpoint}`, formData);
      setSuccessfulUpdate(true);
    } catch (error) {
      if (showSnackbar) {
        showSnackbar(
          SnackbarType.Error,
          t("forgotPassword_problem_with_request")
        );
      }
    }
  };

  return (
    <AuthenticationPageLayout>
      <Card title={t("forgotPassword_create_password_title")}>
        {successfulUpdate && (
          <div className={classes.formSubmitted}>
            <p style={{ marginBottom: "20px" }}>
              {t("forgotPassword_updated_successfully")}
            </p>
            <div className={classes.buttonRow}>
              <BaseButton
                label={t("forgotPassword_return_to_login")}
                onClick={() => {
                  window.location.href = getLoginUrl(urlParams.domain || "");
                }}
              />
            </div>
          </div>
        )}
        {!successfulUpdate && (
          <form
            id="createPasswordForm"
            onSubmit={handleSubmit(onSubmit)}
            className={classes.createPasswordForm}
          >
            <p>{t("forgotPassword_enter_and_save")}</p>
            <p>{t("forgotPassword_password_rules_message")}</p>
            <Input
              control={control}
              label={t("forgotPassword_password_label")}
              name="password"
              type="password"
              placeholder={t("forgotPassword_new_password_label")}
              rules={{
                validate: (value: any) => {
                  return passwordRule(value, getValues("reenter"));
                },
                required: t("forgotPassword_new_password_required"),
              }}
              errorMessage={errors ? errors.password?.message : undefined}
            />
            <Input
              control={control}
              label={t("forgotPassword_reenter_password")}
              name="reenter"
              type="password"
              placeholder={t("forgotPassword_reenter_new_password")}
              rules={{
                validate: (value: any) => {
                  var validateResult = validatePassword(
                    getValues("password"),
                    value
                  );
                  if (!value || (value.length > 0 && validateResult.match))
                    return true;
                  return t("forgotPassword_passwords_do_not_match");
                },
                required: t("forgotPassword_reenter_new_password_error"),
              }}
              errorMessage={errors ? errors.reenter?.message : undefined}
            />
            <div className={classes.buttonRow}>
              <BaseButton
                form="createPasswordForm"
                type="submit"
                label={t("universal_Save")}
                loading={isSubmitting}
              />
            </div>
          </form>
        )}
      </Card>
    </AuthenticationPageLayout>
  );
};

export default CreatePassword;
